import React, { FC, useEffect, useRef, useState } from 'react';

import { Button, Flex, Heading, Stack } from '@chakra-ui/react';

import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';

import { MdGroupAdd } from 'react-icons/md';
import { washHub } from 'store/slices';
import { useIsSuperAdmin } from 'store/slices/auth';

import {
  AddEmployeeModal,
  AddEmployeeModalRef,
} from 'components/modules/AddEmployeeModal';
import { UserPill } from 'components/core/UserPill';

import { SuperAdmins } from './SuperAdmins';
import { WashHubAdmins } from './WashHubAdmins';

import { EmployeesViewProps } from './Employees.props';

const EmployeesView: FC<EmployeesViewProps> = () => {
  const addEmployeeModalRef = useRef<AddEmployeeModalRef>(null);
  const [urlRecord, setUrlRecord] = useState({});
  const [type, setType] = useState<'normal' | 'super_admin'>('normal');
  const selectedWashHubId = washHub.selectors.useSelectedWashHubId();
  const queryUrl = useQueryParams();
  useUrlSharing(urlRecord);

  const isSuperAdmin = useIsSuperAdmin();

  const onCreateEmployee = () => {
    addEmployeeModalRef?.current?.onOpen?.();
  };

  const isType = (value: string): value is 'normal' | 'super_admin' => {
    return ['normal', 'super_admin'].includes(value);
  };

  useEffect(() => {
    const urlParams = {
      ...(type ? { type: `${type}` } : {}),
    };
    setUrlRecord(urlParams);
  }, [type]);

  useEffect(() => {
    const { type } = queryUrl;
    if (type && isType(type)) {
      setType(type);
    }
  }, []);

  return (
    <Flex flex={1} direction="column" color="support.500" overflow="auto">
      <Flex alignItems="baseline" justify="space-between">
        <Heading mb={6} fontSize="24px">
          Employees
        </Heading>
        <Flex align="flex-end">
          <Button
            isDisabled={selectedWashHubId === ''}
            mr={2}
            minW="200px"
            leftIcon={<MdGroupAdd size="20px" />}
            onClick={onCreateEmployee}>
            Create Employee
          </Button>
          <UserPill />
        </Flex>
      </Flex>
      <Stack mb={6} isInline>
        <Button
          w="180px"
          borderRadius="5px"
          variant={type === 'normal' ? 'solid' : 'outline'}
          size="lg"
          onClick={() => setType('normal')}>
          Regular Users
        </Button>
        {isSuperAdmin && (
          <Button
            w="180px"
            borderRadius="5px"
            variant={type === 'super_admin' ? 'solid' : 'outline'}
            size="lg"
            onClick={() => setType('super_admin')}>
            Super Admins
          </Button>
        )}
      </Stack>
      <AddEmployeeModal ref={addEmployeeModalRef} />
      {type === 'normal' ? <WashHubAdmins /> : <SuperAdmins />}
    </Flex>
  );
};

export default EmployeesView;
